import { useBlocker } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import { useSelector } from '@shared/store';
import { MBSentry } from '@shared/util/sentry';
import { Navigator } from './Navigator';
import { getBrowserHistory, getMegaModalHistory } from './history';
import { browserRouteMapping, megaModalRouteMapping } from './routeMapping';
import { RouteName, RouteParamMapping } from './routeParams';

// Pub-sub for main-app browser navigation events.
// Used by components (like MBMegaModal) that live outside <RouterProvider>
// and cannot call TanStack hooks directly.
type NavigationListener = () => void;
const navigationListeners = new Set<NavigationListener>();

export function subscribeToNavigation(listener: NavigationListener): () => void {
  navigationListeners.add(listener);
  return () => navigationListeners.delete(listener);
}

export function notifyNavigationListeners() {
  navigationListeners.forEach(l => l());
}

function getPathForRoute(routeName: RouteName, params: RouteParamMapping[RouteName]): string {
  return browserRouteMapping[routeName]?.(params as any) ?? '';
}

export function useNavigator(): Navigator {
  const browserHistory = getBrowserHistory();
  const megaModalHistory = getMegaModalHistory();

  const navigate = useCallback<Navigator['navigate']>(
    (routeName, params) => {
      if (!!browserRouteMapping[routeName]) {
        browserHistory.push(getPathForRoute(routeName, params));
        megaModalHistory.push('/');
        notifyNavigationListeners();
      } else if (!!megaModalRouteMapping[routeName]) {
        megaModalHistory.push(megaModalRouteMapping[routeName](params as any));
      } else {
        MBSentry.exception({ error: new Error(`invalid web route: ${routeName}`) });
      }
    },
    [browserHistory, megaModalHistory],
  );

  const replace = useCallback<Navigator['replace']>(
    (routeName, params) => {
      if (!!browserRouteMapping[routeName]) {
        browserHistory.replace(getPathForRoute(routeName, params));
        megaModalHistory.push('/');
        notifyNavigationListeners();
      } else if (!!megaModalRouteMapping[routeName]) {
        megaModalHistory.replace(megaModalRouteMapping[routeName](params as any));
      } else {
        MBSentry.exception({ error: new Error(`invalid web route: ${routeName}`) });
      }
    },
    [browserHistory, megaModalHistory],
  );

  const goBack = useCallback(() => {
    if (browserHistory.canGoBack()) {
      browserHistory.back();
    } else {
      browserHistory.replace('/');
    }
  }, [browserHistory]);

  return useMemo<Navigator>(
    () => ({
      navigate,
      replace,
      reset: navigate,
      goBack,
      openMenu: () => megaModalHistory.push('/menu'),
      closeMenu: () => megaModalHistory.push('/'),
    }),
    [goBack, navigate, replace, megaModalHistory],
  );
}

export function useNavigationFocus(): boolean {
  const hidden = useSelector(state => state.appState.isHidden);
  return !hidden;
}

export interface Location<R extends RouteName = RouteName> {
  name: R;
  params?: RouteParamMapping[R];
}

/**
 * Blocks navigation away from the current route until the callback allows it.
 * Return true to allow, false to block.
 */
export function useNavigationBeforeLeave(
  callback: (nextLocation: Location) => boolean | Promise<boolean>,
) {
  useBlocker({
    shouldBlockFn: async ({ next }) => {
      const nextLocation: Location = {
        name: 'Home',
        // @ts-ignore -- TanStack blocker next.params/search union vs Location['params']
        params: { ...next.params, ...next.search },
      };
      const allow = await Promise.resolve(callback(nextLocation));
      return !allow;
    },
    enableBeforeUnload: false,
  });
}
